import html2canvas from 'html2canvas';
import { debugError } from '../utils/debug';

export interface ScreenshotOptions {
    quality?: number;
    width?: number;
    height?: number;
    scale?: number;
}

export interface ScreenshotResult {
    dataUrl: string;
    blob: Blob;
    width: number;
    height: number;
}

/**
 * Service for capturing screenshots of the current page
 */
export class ScreenshotService {
    /**
     * Capture a screenshot of the entire page
     */
    static async capturePage(options: ScreenshotOptions = {}): Promise<ScreenshotResult> {
        const {
            quality = 0.8,
            width = window.innerWidth,
            height = window.innerHeight,
            scale = 1
        } = options;

        try {
            const canvas = await html2canvas(document.body, {
                allowTaint: true,
                useCORS: true,
                width: width,
                height: height,
                backgroundColor: '#ffffff',
                onclone: (clonedDoc: Document) => {
                    // Remove or replace problematic images in the cloned document
                    const images = clonedDoc.querySelectorAll('img');
                    images.forEach((img: HTMLImageElement) => {
                        if (img.src && (img.src.includes('/api/') || img.src.includes('/team/logo'))) {
                            // Replace with a placeholder or remove
                            img.style.display = 'none';
                        }
                    });
                }
            } as any);

            const dataUrl = canvas.toDataURL('image/png', quality);
            const blob = await this.dataUrlToBlob(dataUrl);

            return {
                dataUrl,
                blob,
                width: canvas.width,
                height: canvas.height
            };
        } catch (error) {
            debugError('Error capturing screenshot:', error);
            throw new Error('Failed to capture screenshot');
        }
    }

    /**
     * Capture a screenshot of a specific element
     */
    static async captureElement(element: HTMLElement, options: ScreenshotOptions = {}): Promise<ScreenshotResult> {
        const {
            quality = 0.8,
            scale = 1
        } = options;

        try {
            const canvas = await html2canvas(element, {
                allowTaint: true,
                useCORS: true,
                backgroundColor: '#ffffff'
            });

            const dataUrl = canvas.toDataURL('image/png', quality);
            const blob = await this.dataUrlToBlob(dataUrl);

            return {
                dataUrl,
                blob,
                width: canvas.width,
                height: canvas.height
            };
        } catch (error) {
            debugError('Error capturing element screenshot:', error);
            throw new Error('Failed to capture element screenshot');
        }
    }

    /**
     * Convert data URL to Blob
     */
    private static async dataUrlToBlob(dataUrl: string): Promise<Blob> {
        const response = await fetch(dataUrl);
        return response.blob();
    }

    /**
     * Get browser information for help requests
     */
    static getBrowserInfo(): {
        userAgent: string;
        platform: string;
        language: string;
        screenResolution: string;
        viewportSize: string;
        url: string;
        timestamp: string;
    } {
        return {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenResolution: `${screen.width}x${screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            url: window.location.href,
            timestamp: new Date().toISOString()
        };
    }
}
